import { useMutation } from "@apollo/client";
import { ChangeEventHandler, useEffect, useState } from "react";
import { GetAccountQuery } from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import AiKnowledgeEditor from "@components/ai-knowledge-editor/ai-knowledge-editor";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Heading from "@components/heading/heading";
import SettingsLayout from "@components/settings-layout/settings-layout";
import { assertNonNull } from "@helpers/helpers";
import timezones from "@helpers/timezones";

import updateUserMutation from "../graphql/update-user-mutation";

const linkOptions = [
  {
    value: "always",
    label: "For all events",
  },
  {
    value: "meetings_with_topics",
    label: "Only for events with agenda items in Topicflow",
  },
  {
    value: "never",
    label: "Never add links to my calendar events",
  },
];

const Preferences = ({ me }: { me: GetAccountQuery["me"] }) => {
  const currentUser = currentUserVar();
  const selectedLinkOption = currentUser.eventLinkInDescriptionPreference;
  const timezoneOptions = timezones.map((timezone) => ({
    label: timezone,
    value: timezone,
  }));

  const [timezone, setTimezone] = useState<string | null | undefined>(null);
  const [updateUser] = useMutation(updateUserMutation);

  const handleChangeTimezone = (option: ComboboxGenericOption<string>) => {
    if (!option) return;
    const value = option.value;
    setTimezone(value);
    updateUser({
      variables: { userId: assertNonNull(me).id, timezone: value },
    });
  };

  const handleChangeSelectedLinkOption: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    updateUser({
      variables: {
        userId: currentUser.id,
        eventLinkInDescriptionPreference: e.target.value,
      },
    });
  };

  const handleSaveAiKnowledge = async (jsonValue: string) => {
    if (assertNonNull(me).aiGuidelines === jsonValue) return;

    await updateUser({
      variables: {
        userId: assertNonNull(me).id,
        aiGuidelines: jsonValue,
      },
    });
  };

  useEffect(() => {
    setTimezone(assertNonNull(me).timezone);
  }, [me]);

  return (
    <div>
      <Heading small title="Preferences" />

      <SettingsLayout.Container>
        <SettingsLayout.Row>
          <SettingsLayout.Left label="* Only affects events where you are the organizer">
            Automatically add a Topicflow link to calendar events
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="flex flex-col">
              {linkOptions.map(({ value, label }) => (
                <div
                  key={value}
                  className="text-sm text-gray-800 flex items-center py-1"
                >
                  <input
                    type="radio"
                    name="link-option"
                    id={`input-link-option-${value}`}
                    value={value}
                    defaultChecked={selectedLinkOption === value}
                    onChange={handleChangeSelectedLinkOption}
                  />
                  <label
                    className="ml-2 cursor-pointer"
                    htmlFor={`input-link-option-${value}`}
                  >
                    {label}
                  </label>
                </div>
              ))}
              <div className="text-gray-500 text-sm mt-2">
                Events created using Topicflow will always include a link to the
                agenda.
              </div>
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Timezone</SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="lg:w-96">
              {timezone && (
                <ComboboxGeneric<string>
                  value={
                    timezoneOptions.find(
                      (option) => option.value === timezone,
                    ) || timezoneOptions[0]
                  }
                  options={timezoneOptions}
                  onChangeValue={handleChangeTimezone}
                  aria-label="Timezone select"
                />
              )}
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left label="Add knowledge specific to your account to personalize Topicflow AI.">
            AI Preferences
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="w-full">
              <AiKnowledgeEditor
                value={assertNonNull(me).aiGuidelines || null}
                onSave={handleSaveAiKnowledge}
                label="AI Preferences"
                description="Add details about your role, preferences, and working context so Topicflow AI can personalize responses for you."
                placeholder="Add context Topicflow AI should know about you, your role, and your preferences."
              />
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      </SettingsLayout.Container>
    </div>
  );
};

export default Preferences;
